import React from 'react';
import { motion } from 'framer-motion';
import { GlowButton } from './GlowButton';
import { useCurrencyPrice } from '../hooks/useCurrencyPrice';
import { useLanguage } from '../contexts/LanguageContext';
export function FooterCTA() {
  const {
    price,
    originalPrice,
    loading
  } = useCurrencyPrice();
  const {
    t,
    checkoutUrl
  } = useLanguage();
  return <footer className="relative w-full overflow-hidden" data-id="element-93">
      {/* CTA Section */}
      <section id="obtener-pack" className="relative py-10 sm:py-20" data-id="element-94">
        {/* Background radial glow */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-95">
          <div className="w-[700px] h-[700px] bg-gradient-radial from-purple-electric/[0.06] via-gold/[0.02] to-transparent rounded-full blur-[80px]" data-id="element-96" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center" data-id="element-97">
          {/* Decorative line */}
          <motion.div initial={{
          scaleX: 0
        }} whileInView={{
          scaleX: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.2,
          ease: [0.22, 1, 0.36, 1]
        }} className="w-24 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent mx-auto mb-6 sm:mb-10" data-id="element-98" />

          <motion.h2 initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 1,
          ease: [0.22, 1, 0.36, 1]
        }} className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5 sm:mb-8" data-id="element-99">
            {t('footer.title.part1')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-gold-light to-gold" data-id="element-100">
              {t('footer.title.highlight')}
            </span>{' '}
            {t('footer.title.part2')}
          </motion.h2>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex flex-col items-center justify-center gap-4" data-id="element-101">
            {/* Price display */}
            <div className="flex flex-col items-center gap-1.5 mb-1" data-id="element-102">
              <div className="flex items-center gap-3" data-id="element-103">
                <span className="font-sans text-white/30 text-base line-through" data-id="element-104">
                  {t('hero.priceBefore')} {loading ? '$60 USD' : originalPrice}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-flame/15 border border-flame/25 text-flame-light text-xs font-sans font-medium tracking-wide" data-id="element-105">
                  {t('hero.limitedTime')}
                </span>
              </div>
              <span className="font-serif text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold via-gold-light to-gold" data-id="element-106">
                {loading ? '$14.99 USD' : price}
              </span>
              {/* Bonus badge */}
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal/15 border border-teal/30 text-white/70 font-sans font-medium tracking-wide text-[20px]" data-id="element-107">
                {t('hero.bonusBadge')}
              </span>
            </div>

            <div id="cta-pago" data-id="element-108">
              <GlowButton href={checkoutUrl} variant="gold" data-id="element-109">
                {t('hero.cta')}
              </GlowButton>
            </div>
            <p className="font-sans text-[11px] text-white/30 tracking-wide text-center" data-id="element-110">
              {t('hero.trustLine')}
            </p>
            <p className="font-sans text-white/30 text-sm" data-id="element-111">
              {t('hero.ebook')} + {t('hero.audiobook')}
            </p>
            {/* Tagline below button */}
            <motion.p initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="font-sans text-white/45 text-sm sm:text-base max-w-sm mx-auto leading-relaxed text-center" data-id="element-112">
              {t('footer.tagline.part1')}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-gold-light to-gold font-serif text-xl sm:text-2xl font-bold leading-relaxed" data-id="element-113">
                {t('footer.tagline.part2')}
              </span>
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Minimal footer */}
      <div className="relative border-t border-white/[0.04]" data-id="element-114">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-115">
          <div className="flex items-center gap-2" data-id="element-116">
            <span className="font-serif text-lg font-bold text-gold/60" data-id="element-117">
              DMT
            </span>
            <span className="text-white/15 text-xs font-sans" data-id="element-118">
              {t('nav.subtitle')}
            </span>
          </div>

          <div className="flex items-center gap-6" data-id="element-119">
            <a href="#libro" className="font-sans text-xs text-white/25 hover:text-gold/60 transition-colors duration-300" data-id="element-120">
              {t('nav.book')}
            </a>
            <a href="#autores" className="font-sans text-xs text-white/25 hover:text-gold/60 transition-colors duration-300" data-id="element-121">
              {t('nav.authors')}
            </a>
          </div>

          <p className="font-sans text-xs text-white/15" data-id="element-122">
            {t('footer.copyright')}
          </p>
        </div>
      </div>
    </footer>;
}